import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { MessageService } from "common";
import { DocuSignSigningFacade } from "../domain/+state/docusign-signing.facade";
import {
  DocuSignCeremonyEvent,
  DocuSignReturnEvent,
  EnvelopeRecipientStatus,
  SigningSessionData,
  SigningSubject,
  SigningSubjectKind,
  signAgreementRoute,
} from "../domain/models/docusign-signing.model";
import { DocuSignSdkLoader } from "../infrastructure/docusign-sdk.loader";

/** The DocuSign branch of the sign-agreement step: the ceremony is embedded through the DocuSign.js SDK. */
@UntilDestroy()
@Component({
  selector: "ifp-docusign-sign-step",
  templateUrl: "./docusign-sign-step.component.html",
  styleUrls: ["./docusign-sign-step.component.scss"],
  standalone: false,
})
export class DocuSignSignStepComponent implements OnInit, OnDestroy {
  /** The loan, or the MSA application, being signed. */
  @Input() subject: SigningSubject;

  /** The figures the agreement quotes, from the parent. */
  @Input() summary: DocuSignSignStepSummary;

  /** Where the SDK mounts the ceremony; rendered unconditionally, as the SDK cannot mount into a detached element. */
  @ViewChild("ceremony") ceremonyHost: ElementRef<HTMLElement>;

  /** Set once the initial read shows this signer has nothing left to do. */
  alreadySigned = false;

  /** Other owners the loan is still waiting on, by name. */
  outstandingSignerNames: string[] = [];

  /** {@link outstandingSignerNames} as one readable string. */
  outstandingSigners = "";

  /** True while an attempt is in flight: the initial read as well as the mint that follows it. */
  starting = false;

  /** Set when DocuSign reports the signer has finished inside the frame. */
  finishing = false;

  /** True once the SDK has mounted a ceremony, so a second mint stays away. */
  mounted = false;

  /** An attempt ended with neither a mounted ceremony nor a completed signature; renders the retry placeholder. */
  failed = false;

  /** An MSA, signed against its application, rather than a loan agreement. */
  isApplication = false;

  /** "ABC Company - L-00000012"; the entity alone for an MSA, which has no loan number. */
  summaryTitle = "";

  /** Guards the two callbacks that resume outside a subscription. See {@link refresh}. */
  private destroyed = false;

  /** A mint is in flight; the re-entrancy guard that keeps a single-use signing URL from being minted twice. */
  private minting = false;

  constructor(
    private readonly facade: DocuSignSigningFacade,
    private readonly messageService: MessageService,
    private readonly sdkLoader: DocuSignSdkLoader,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isApplication = this.subject?.kind === SigningSubjectKind.Application;
    this.summaryTitle = [this.summary?.entityName, this.summary?.loanNumber].filter(part => !!part).join(" - ");
    this.starting = true;

    this.facade
      .getSigningState$(this.subject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (state) => {
          this.alreadySigned = state?.recipientStatus === EnvelopeRecipientStatus.Completed;
          this.setOutstandingSigners(state?.outstandingSignerNames);

          if (this.alreadySigned) {
            this.endAttempt();

            return;
          }

          this.start();
        },
        // Swallowed on purpose: no envelope yet is the normal first visit, and the mint raises it.
        error: () => this.start(),
      });
  }

  /** Re-mints a URL after a session expired inside the ceremony, without leaving the page. */
  retry(): void {
    if (this.minting) {
      return;
    }

    this.mounted = false;
    this.start();
  }

  /** The SDK's `sessionEnd`; only a collected signature or a decline is terminal. */
  private onSessionEnd(payload: DocuSignCeremonyEvent | string): void {
    const outcome =
      typeof payload === "string" ? payload : payload?.sessionEndType ?? payload?.type ?? payload?.event;

    if (outcome !== DocuSignReturnEvent.SigningComplete && outcome !== DocuSignReturnEvent.Decline) {
      return;
    }

    this.finishing = true;
    this.refresh();

    // Merge keeps backTo, so an MSA signer can be sent back to my.levelesq afterwards.
    void this.router.navigate([`${signAgreementRoute(this.subject)}/return`], {
      queryParams: { event: outcome },
      queryParamsHandling: "merge",
    });
  }

  /** Change detection for the SDK paths that resume outside Angular's zone. */
  private refresh(): void {
    if (this.destroyed) {
      return;
    }

    this.changeDetector.detectChanges();
  }

  /** One themed colour read off the host's custom properties; undefined leaves DocuSign's default. */
  private readBrandingColour(property: string): string | undefined {
    const value = getComputedStyle(this.ceremonyHost.nativeElement).getPropertyValue(property).trim();

    return value.length > 0 ? value : undefined;
  }

  private setOutstandingSigners(names: string[]): void {
    this.outstandingSignerNames = names ?? [];
    this.outstandingSigners = this.outstandingSignerNames.join(", ");
  }

  /** Ends the current attempt and decides what the ceremony area shows next. */
  private endAttempt(): void {
    this.minting = false;
    this.starting = false;
    this.failed = !this.mounted && !this.alreadySigned;
  }

  private start(): void {
    if (this.minting) {
      return;
    }

    if (this.mounted) {
      this.endAttempt();

      return;
    }

    this.minting = true;
    this.starting = true;
    this.failed = false;

    this.facade
      .startSigningSession$(this.subject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (session) => this.present(session),
        error: (error: unknown) => {
          this.endAttempt();
          this.messageService.error(error);
        },
      });
  }

  private present(session: SigningSessionData): void {
    this.setOutstandingSigners(session?.outstandingSignerNames);

    // Null means nothing to sign — this signer has completed or declined.
    if (!session?.signingUrl) {
      this.alreadySigned = session?.recipientStatus === EnvelopeRecipientStatus.Completed;
      this.endAttempt();

      if (!this.alreadySigned) {
        this.messageService.error("This agreement can no longer be signed. Please contact your business advisor.");
      }

      return;
    }

    if (!session.signingBundleUrl || !session.integrationKey) {
      this.endAttempt();
      this.messageService.error("The agreement could not be opened. Nothing has been signed — please try again.");

      return;
    }

    void this.mount(session);
  }

  private async mount(session: SigningSessionData): Promise<void> {
    try {
      const sdk = await this.sdkLoader.load(session.signingBundleUrl, session.integrationKey);

      if (this.destroyed || !this.ceremonyHost) {
        return;
      }

      const signing = sdk.signing({
        url: session.signingUrl,
        displayFormat: "focused",
        style: {
          branding: {
            primaryButton: {
              backgroundColor: this.readBrandingColour("--docusign-primary-background"),
              color: this.readBrandingColour("--docusign-primary-foreground"),
            },
          },
          signingNavigationButton: { finishText: "Finish", position: "bottom-center" },
        },
      });

      signing.on("sessionEnd", (payload) => this.onSessionEnd(payload));

      signing.mount(this.ceremonyHost.nativeElement);

      this.mounted = true;
    } catch (error: unknown) {
      this.messageService.error(error);
    } finally {
      this.endAttempt();
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }
}

/** The offer figures the agreement quotes, as the summary card presents them. */
export interface DocuSignSignStepSummary {
  /** "ABC Company - L-00000012" is built from these two. */
  entityName: string;
  /** Absent for an MSA, which has no loan yet. */
  loanNumber?: string;
  amount: number;
  /** "Credit limit" for a line of credit, "Loan amount" for a term loan. */
  amountLabel: string;
  repaymentTerm: number;
  paymentFrequencyLabel: string;
  /** Named in the explainer: "It includes {{state}} disclosures and your {{product}} Agreement." */
  stateName?: string;
  productName: string;
}
